import { useScroll } from '@use-gesture/react';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const SCROLL_BUFFER = 4;

export const initialState = {
  isOverflowedX: false,
  isOverflowedY: false,
  isOverflowed: false,
  isScrolledX: false,
  isScrolledY: false,
  isScrolled: false,
  isScrolling: false,
  isTop: true,
  isLeft: true,
  isRight: false,
  isBottom: false,
  scrollLeft: 0,
  scrollTop: 0,
};

export const getScrollState = (
  target: HTMLElement,
  { offset: [xOffset, yOffset] = [0, 0], active = false } = {},
) => {
  const { offsetWidth, scrollWidth, offsetHeight, scrollHeight } = target;
  const isOverflowedX = offsetWidth < scrollWidth;
  const isOverflowedY = offsetHeight < scrollHeight;
  const isScrolledX = xOffset >= SCROLL_BUFFER;
  const isScrolledY = yOffset >= SCROLL_BUFFER;

  return {
    isOverflowedX,
    isOverflowedY,
    isOverflowed: isOverflowedX || isOverflowedY,
    isScrolledX,
    isScrolledY,
    isScrolled: isScrolledX || isScrolledY,
    isScrolling: active,
    isTop: !isScrolledY,
    isLeft: !isScrolledX,
    isRight: offsetWidth + xOffset + SCROLL_BUFFER >= scrollWidth,
    isBottom: offsetHeight + yOffset + SCROLL_BUFFER >= scrollHeight,
    scrollLeft: xOffset,
    scrollTop: yOffset,
  };
};

const useScrollObserver = <T extends HTMLElement | null>(
  ref?: React.ForwardedRef<HTMLElement>,
) => {
  const [scrollState, setScrollState] = useState(initialState);
  const resizeObserver = useMemo<ResizeObserver>(
    () =>
      new ResizeObserver(
        debounce(
          (entries) => {
            for (const entry of entries) {
              if (!isMounted.current) {
                return;
              }
              setScrollState(getScrollState(entry.target as HTMLElement));
            }
          },
          250,
          {
            leading: true,
            trailing: true,
          },
        ),
      ),
    [],
  );
  const observedNode = useRef<T | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => resizeObserver.disconnect();
  }, [resizeObserver]);

  const useScrollCallback = useCallback(
    ({
      target,
      offset,
      active,
    }: {
      target: EventTarget;
      offset: [number, number];
      active: boolean;
    }) => {
      setScrollState(getScrollState(target as HTMLElement, { offset, active }));
    },
    [],
  );

  useScroll(useScrollCallback, {
    target: observedNode,
  });

  const observeScroll = useCallback(
    (node: T) => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          ref.current = node;
        }
      }

      if (observedNode.current) {
        resizeObserver.unobserve(observedNode.current);
        isMounted.current = false;
      }

      if (node !== null && node !== observedNode.current) {
        isMounted.current = true;
        resizeObserver.observe(node);
        observedNode.current = node;
        setScrollState(getScrollState(node));
      }
    },
    // Listing `ref` to satisfy eslint, but it won't trigger re-definition of the callback
    [resizeObserver, ref],
  );

  return {
    scrollState,
    refs: { observeScroll, observedNode },
  };
};

export default useScrollObserver;
